import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, Alert, CircularProgress, Stack,
  IconButton, InputAdornment,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import myDriveService, { WebDAVInfo } from '../services/myDriveService';
import { useMessage } from '../../../../contexts/MessageContext';

interface Props {
  open: boolean;
  onClose: () => void;
  canUpdate: boolean;
  onStatusChange?: () => void;   // 啟用/關閉成功後通知 parent 刷新狀態
}

export default function WebDAVSettingsDialog({ open, onClose, canUpdate, onStatusChange }: Props) {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [info, setInfo]         = useState<WebDAVInfo | null>(null);
  const [loading, setLoading]   = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const loadInfo = () => {
    setLoading(true);
    myDriveService.getWebdavInfo()
      .then(setInfo)
      .catch(showApiError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) { setPassword(''); setShowPassword(false); return; }
    loadInfo();
  }, [open]); // eslint-disable-line

  const handleEnable = async () => {
    if (!password) return;
    try {
      await myDriveService.enableWebdav(password);
      showSuccess('SYS330011');   // WebDAV 已啟用
      setPassword('');
      loadInfo();
      onStatusChange?.();
    } catch (err) {
      showApiError(err);          // ERR330026 等
      setPassword('');
    }
  };

  const handleDisable = async () => {
    try {
      await myDriveService.disableWebdav();
      showSuccess('SYS330012');   // WebDAV 已關閉
      loadInfo();
      onStatusChange?.();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudIcon /> {t('myDrive.webdavSettings', 'WebDAV 設定')}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}

        {!loading && info && info.enabled && (
          <Stack spacing={2}>
            <Alert severity="success">{t('myDrive.webdavEnabled', 'WebDAV 已啟用')}</Alert>
            <Box>
              <Typography variant="body2">
                {t('myDrive.webdavConnectInfo', '使用以下資訊連線：')}
              </Typography>
              <Box sx={{ fontFamily: 'monospace', mt: 1, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: 13 }}>
                <div>URL：{info.url}</div>
                <div>{t('myDrive.userAccount', '帳號')}：{info.username}</div>
                <div>{t('common.password', '密碼')}：{t('myDrive.webdavSsoHint', '您的登入密碼')}</div>
              </Box>
            </Box>
            <Alert severity="info" icon={false}>
              {t('myDrive.webdavSsoNote',
                '提示：WebDAV 走 SSO，使用您的登入密碼。變更登入密碼時 WebDAV 也會自動同步，記得更新外部 client config。')}
            </Alert>
            <Alert severity="warning" icon={false}>
              {t('myDrive.webdavHttpsWarning',
                '⚠️ 請確保 WebDAV client 透過 HTTPS 連線，Basic Auth 走 HTTP 會明文洩漏密碼。')}
            </Alert>
          </Stack>
        )}

        {!loading && info && !info.enabled && (
          <Stack spacing={2}>
            <Alert severity="info">{t('myDrive.webdavDisabled', 'WebDAV 未啟用')}</Alert>
            <Typography variant="body2">
              {t('myDrive.webdavEnablePrompt',
                '輸入您的登入密碼以啟用 WebDAV。WebDAV 將使用您的登入密碼（SSO）。')}
            </Typography>
            <TextField
              type={showPassword ? 'text' : 'password'}
              label={t('myDrive.loginPassword', '登入密碼')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleEnable()}
              autoFocus fullWidth
              disabled={!canUpdate}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(s => !s)}
                      edge="end" size="small" tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {info?.enabled && canUpdate && (
          <Button color="error" onClick={handleDisable} sx={{ mr: 'auto' }}>
            {t('myDrive.webdavDisable', '關閉 WebDAV')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.close', '關閉')}</Button>
        {!info?.enabled && canUpdate && (
          <Button variant="contained" disabled={!password} onClick={handleEnable}>
            {t('myDrive.webdavEnable', '啟用 WebDAV')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
